// src/pages/AdminDashboard.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import API from "../../api.js";
import {
  FaBars,
  FaUserMd,
  FaUserInjured,
  FaCalendarAlt,
  FaSignOutAlt,
  FaCalendarPlus,
  FaBell,
  FaSyncAlt,
  FaCheckCircle,
  FaTimesCircle,
  FaBuilding,
} from "react-icons/fa";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as PieTooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import CountUp from "react-countup";
import "../../App.css";

const AdminDashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [stats, setStats] = useState({
    totalDoctors: 0,
    totalPatients: 0,
    totalDepartments: 0,
    totalAppointments: 0,
    totalConfirmed: 0,
    totalRejected: 0,
    totalCompleted: 0,
    todaysCreatedAppointments: 0,
    todaysScheduledConfirmed: 0,
    newAppointmentsCount: 0,
    monthly: [],
  });
  const [loading, setLoading] = useState(true);
  const pollIntervalMs = 30000; // 30s
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const [blinkNotif, setBlinkNotif] = useState(false);

  const { userInfo } = useSelector((state) => state.auth || {});
  const prevNewCount = useRef(0);

  const fetchStats = useCallback(
    async (opts = { silent: false }) => {
      if (!opts.silent) setLoading(true);
      try {
        const { data } = await API.get("/admin/stats");

        const prevCount = prevNewCount.current ?? stats.newAppointmentsCount;
        if ((data.newAppointmentsCount ?? 0) > (prevCount ?? 0)) {
          setBlinkNotif(true);
          setTimeout(() => setBlinkNotif(false), 1800);
        }
        prevNewCount.current = data.newAppointmentsCount ?? 0;

        setStats({
          totalDoctors: data.totalDoctors ?? 0,
          totalPatients: data.totalPatients ?? 0,
          totalDepartments: data.totalDepartments ?? 0,
          totalAppointments: data.totalAppointments ?? 0,
          totalConfirmed: data.totalConfirmed ?? 0,
          totalRejected: data.totalRejected ?? 0,
          totalCompleted: data.totalCompleted ?? 0,
          todaysCreatedAppointments: data.todaysCreatedAppointments ?? 0,
          todaysScheduledConfirmed: data.todaysScheduledConfirmed ?? 0,
          newAppointmentsCount: data.newAppointmentsCount ?? 0,
          monthly: data.monthly ?? [],
        });
      } catch (err) {
        console.error("Error fetching admin stats:", err);
      } finally {
        setLoading(false);
      }
    },
    [stats.newAppointmentsCount]
  );

  useEffect(() => {
    fetchStats({ silent: false });
    const interval = setInterval(
      () => fetchStats({ silent: true }),
      pollIntervalMs
    );
    return () => clearInterval(interval);
  }, [fetchStats]);

  const COLORS = ["#00796B", "#0288D1", "#FFCA28", "#8E24AA"];
  const pieData = useMemo(
    () => [
      { name: "Doctors", value: stats.totalDoctors },
      { name: "Patients", value: stats.totalPatients },
      { name: "Departments", value: stats.totalDepartments },
    ],
    [stats.totalDoctors, stats.totalPatients, stats.totalDepartments]
  );

  const monthlyChart = useMemo(() => {
    if (!Array.isArray(stats.monthly) || stats.monthly.length === 0) {
      const now = new Date();
      return Array.from({ length: 6 }).map((_, i) => {
        const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
        const label = d.toLocaleString("default", {
          month: "short",
          year: "numeric",
        });
        return { month: label, count: 0 };
      });
    }
    return stats.monthly.map((m) => ({ month: m.month, count: m.count ?? 0 }));
  }, [stats.monthly]);

  if (loading)
    return <div className="loading-screen">Loading Dashboard...</div>;

  return (
    <div className="admin-dashboard-container">
      {/* Sidebar */}
      <aside className={`admin-sidebar ${sidebarOpen ? "open" : "closed"}`}>
        <div className="sidebar-header">
          <h3 className="sidebar-title">🏥 Shoaib Hospital</h3>
          <button
            className="menu-close-btn"
            onClick={() => setSidebarOpen(false)}
          >
            ✕
          </button>
        </div>

        <nav className="sidebar-links">
          <Link to="/admin/dashboard">Dashboard</Link>
          <Link to="/admin/appointments">Appointments</Link>
          <Link to="/admin/doctors">Doctors</Link>
          <Link to="/admin/departments">Departments</Link>
          <Link to="/admin/manage-medicines">Medicines</Link>
        </nav>

        <button className="logout-btn">
          <FaSignOutAlt /> Logout
        </button>
      </aside>

      {sidebarOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      {/* Main */}
      <div className="admin-main">
        <header className="admin-header shadow-sm">
          <div className="header-left">
            <button
              className="menu-btn"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              <FaBars />
            </button>
            <h6 className="admin-title">
              Welcome, {userInfo?.name || "Admin"}
            </h6>
          </div>

          <div className="header-right">
            <div className="notif-wrapper">
              <button
                className={`btn btn-link ${blinkNotif ? "blink-notif" : ""}`}
                title="New appointments"
                onClick={() => setShowNotifPanel((s) => !s)}
              >
                <FaBell size={20} />
              </button>
              {stats.newAppointmentsCount > 0 && (
                <span className="notif-badge">
                  {stats.newAppointmentsCount}
                </span>
              )}
            </div>
            <button
              className="btn btn-link"
              title="Refresh stats"
              onClick={() => fetchStats({ silent: false })}
            >
              <FaSyncAlt size={20} />
            </button>
          </div>
        </header>

        {/* Notification panel */}
        {showNotifPanel && (
          <div className="notif-panel">
            <div className="notif-title">Notifications</div>
            <div className="notif-body">
              <div>
                New (pending) appointments:{" "}
                <strong>{stats.newAppointmentsCount}</strong>
              </div>
              <div>
                Created Today:{" "}
                <strong>{stats.todaysCreatedAppointments}</strong>
              </div>
              <div>
                Confirmed Today:{" "}
                <strong>{stats.todaysScheduledConfirmed}</strong>
              </div>
            </div>
            <div className="notif-footer">
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={() => {
                  fetchStats();
                  setShowNotifPanel(false);
                }}
              >
                Refresh
              </button>
            </div>
          </div>
        )}

        {/* Cards section */}
        <div className="dashboard-content">
          {/* Appointment-related cards (Row 1) */}
          <div className="dashboard-cards appointment-cards">
            <div className="stat-card appointment-blue">
              <h5>
                <FaCalendarAlt /> Appointments Today
              </h5>
              <h2>
                <CountUp end={stats.todaysCreatedAppointments} duration={1} />
              </h2>
              <small>Confirmed today: {stats.todaysScheduledConfirmed}</small>
            </div>
            <div className="stat-card appointment-blue">
              <h5>
                <FaCalendarPlus /> Total Appointments
              </h5>
              <h2>
                <CountUp end={stats.totalAppointments} duration={1} />
              </h2>
            </div>
            <div className="stat-card appointment-green">
              <h5>
                <FaCheckCircle /> Completed
              </h5>
              <h2>
                <CountUp end={stats.totalCompleted} duration={1} />
              </h2>
            </div>
            <div className="stat-card appointment-purple">
              <h5>
                <FaCheckCircle /> Confirmed
              </h5>
              <h2>
                <CountUp end={stats.totalConfirmed} duration={1} />
              </h2>
            </div>
            <div className="stat-card appointment-red">
              <h5>
                <FaTimesCircle /> Rejected
              </h5>
              <h2>
                <CountUp end={stats.totalRejected} duration={1} />
              </h2>
            </div>
          </div>

          {/* Doctors/Patients/Departments cards (Row 2) */}
          <div className="dashboard-cards entity-cards">
            <div className="stat-card entity-blue">
              <h5>
                <FaUserMd /> Total Doctors
              </h5>
              <h2>
                <CountUp end={stats.totalDoctors} duration={1} />
              </h2>
            </div>
            <div className="stat-card entity-green">
              <h5>
                <FaUserInjured /> Total Patients
              </h5>
              <h2>
                <CountUp end={stats.totalPatients} duration={1} />
              </h2>
            </div>
            <div className="stat-card entity-orange">
              <h5>
                <FaBuilding /> Departments
              </h5>
              <h2>
                <CountUp end={stats.totalDepartments} duration={1} />
              </h2>
            </div>
          </div>

          {/* Charts side by side */}
          <div className="charts-wrapper">
            <div className="chart-item">
              <h4>Appointments — Monthly Progress</h4>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart
                  data={monthlyChart}
                  margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="#0f6b70"
                    strokeWidth={3}
                    dot={{ r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-item">
              <h4>Overview</h4>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    outerRadius={70}
                    fill="#8884d8"
                    label
                  >
                    {pieData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <PieTooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      {/* Inline CSS */}
      <style>{`
        .admin-header { display:flex; justify-content:space-between; align-items:center;
          padding:0.75rem 1.25rem; background:#fff; box-shadow:0 2px 8px rgba(0,0,0,0.05);
          position:sticky; top:0; z-index:20; }
        .header-left { display:flex; align-items:center; gap:12px; }
        .header-right { display:flex; align-items:center; gap:12px; }
        .notif-wrapper { position: relative; }
        .notif-badge {
          position: absolute; top: -6px; right: -6px; background: #dc3545;
          color: #fff; border-radius: 50%; min-width: 20px; height: 20px;
          display: flex; align-items: center; justify-content: center; font-size: 12px;
          padding: 0 5px;
        }
        .notif-panel {
          position: absolute; right: 20px; top: 72px; width: 340px; background: #fff;
          border-radius: 6px; padding: 12px; box-shadow: 0 6px 18px rgba(0,0,0,0.08); z-index: 40;
        }
        .notif-title { font-weight:700; margin-bottom:8px; }
        .notif-body div { padding: 8px 0; border-bottom: 1px solid #eee; }
        .notif-footer { text-align: right; margin-top: 8px; }

        .dashboard-cards { display: flex; flex-wrap: wrap; gap:18px; margin:16px 0; }
        .appointment-cards { justify-content: flex-start; }
        .entity-cards { justify-content: flex-start; }

        .stat-card {
          background:white; padding:20px; border-radius:12px; box-shadow:0 3px 10px rgba(0,0,0,0.1); 
          text-align:center; transition:0.2s; flex:1 1 200px;
        }
        .stat-card:hover { transform: translateY(-3px); }
        .stat-card h5 { font-size:16px; font-weight:500; color:#666; display:flex; align-items:center; justify-content:center; gap:6px; }
        .stat-card h2 { font-size:32px; font-weight:bold; margin-top:10px; }

        /* Appointment colors */
        .appointment-blue { color:#007bff; border-top:4px solid #007bff; }
        .appointment-green { color:#28a745; border-top:4px solid #28a745; }
        .appointment-purple { color:#6f42c1; border-top:4px solid #6f42c1; }
        .appointment-red { color:#dc3545; border-top:4px solid #dc3545; }

        /* Entity colors */
        .entity-blue { color:#007bff; border-top:4px solid #007bff; }
        .entity-green { color:#28a745; border-top:4px solid #28a745; }
        .entity-orange { color:#fd7e14; border-top:4px solid #fd7e14; }

        .charts-wrapper { display:flex; gap:24px; flex-wrap: wrap; margin-top:24px; }
        .chart-item { flex:1 1 400px; background:#fff; padding:16px; border-radius:10px; box-shadow:0 6px 18px rgba(0,0,0,0.02); }

        .blink-notif { animation: blink 0.6s linear 3; }
        @keyframes blink { 0% { opacity:1 } 50% { opacity:0.2 } 100% { opacity:1 } }
        .loading-screen { padding:48px; text-align:center; font-weight:600; font-size:18px; }
      `}</style>
    </div>
  );
};

export default AdminDashboard;
